import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  House, Buildings, CreditCard, Users, Receipt, ChartLine, Stack,
  Coins, ListChecks, Broadcast, Gear, SignOut, BookOpen, Wallet,
  FileArrowUp, Plug, List,
} from "@phosphor-icons/react";
import { auth } from "@/lib/api";
import { ADMIN, ADMIN_LOGIN_PATH } from "@/lib/routes";
import { BlonixWordmark } from "@/components/BlonixLogo";
import { Sheet, SheetContent } from "@/components/ui/sheet";

const NAV = [
  { to: ADMIN.home, label: "Overview", icon: House, end: true },
  { to: ADMIN.merchants, label: "Merchants", icon: Buildings },
  { to: ADMIN.balanceFunding, label: "Balance funding", icon: Wallet },
  { to: ADMIN.kybQueue, label: "KYB Queue", icon: FileArrowUp },
  { to: ADMIN.cards, label: "Cards", icon: CreditCard },
  { to: ADMIN.users, label: "Cardholders", icon: Users },
  { to: ADMIN.transactions, label: "Transactions", icon: Receipt },
  { to: ADMIN.analytics, label: "Analytics", icon: ChartLine },
];

const NAV2 = [
  { to: ADMIN.cardTypes, label: "Card Types", icon: Stack },
  { to: ADMIN.fees, label: "Fees", icon: Coins },
  { to: ADMIN.providers, label: "Providers", icon: Plug },
  { to: ADMIN.auditLogs, label: "Audit Logs", icon: ListChecks },
  { to: ADMIN.webhooks, label: "Webhooks", icon: Broadcast },
  { to: ADMIN.apiDocs, label: "API Reference", icon: BookOpen },
  { to: ADMIN.settings, label: "Settings", icon: Gear },
];

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = auth.user();
  const [navOpen, setNavOpen] = useState(false);

  const onLogout = async () => {
    await auth.logout();
    navigate(ADMIN_LOGIN_PATH);
  };

  return (
    <div className="min-h-screen flex bg-zinc-100" data-testid="app-shell">
      <aside
        className="hidden lg:flex w-64 shrink-0 bg-zinc-950 text-zinc-300 flex-col fixed inset-y-0 left-0 z-30"
        data-testid="sidebar"
      >
        <AdminSidebar user={user} onLogout={onLogout} />
      </aside>

      <Sheet open={navOpen} onOpenChange={setNavOpen}>
        <SheetContent side="left" className="w-72 max-w-[85vw] p-0 bg-zinc-950 border-zinc-900 text-zinc-300 [&>button]:text-zinc-400 [&>button]:hover:text-white">
          <AdminSidebar
            user={user}
            onLogout={onLogout}
            onNavigate={() => setNavOpen(false)}
          />
        </SheetContent>
      </Sheet>

      <div className="flex-1 min-w-0 flex flex-col lg:ml-64">
        <header className="lg:hidden sticky top-0 z-20 flex items-center gap-3 px-4 py-3 bg-white border-b border-zinc-200">
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            className="h-9 w-9 inline-flex items-center justify-center rounded-sm border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
            aria-label="Open navigation"
          >
            <List size={20} />
          </button>
          <BlonixWordmark className="h-4 w-auto" />
        </header>

        <main className="flex-1 min-w-0">
          <div key={location.pathname} className="page-enter">{children}</div>
        </main>
      </div>
    </div>
  );
}

function AdminSidebar({ user, onLogout, onNavigate }) {
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-6 py-6 border-b border-zinc-900 shrink-0">
        <BlonixWordmark className="h-5 w-auto" />
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-2">Card Platform</div>
      </div>

      <nav className="flex-1 overflow-y-auto sb-scroll px-3 py-4 space-y-6 min-h-0">
        <div>
          <div className="px-3 mb-2 text-[10px] uppercase tracking-[0.18em] text-zinc-600">Operations</div>
          <ul className="space-y-0.5">
            {NAV.map((it) => <SideLink key={it.to} {...it} onNavigate={onNavigate} />)}
          </ul>
        </div>
        <div>
          <div className="px-3 mb-2 text-[10px] uppercase tracking-[0.18em] text-zinc-600">Configuration</div>
          <ul className="space-y-0.5">
            {NAV2.map((it) => <SideLink key={it.to} {...it} onNavigate={onNavigate} />)}
          </ul>
        </div>
      </nav>

      <div className="border-t border-zinc-900 p-3 shrink-0">
        <div className="px-3 py-2 flex items-center gap-3">
          <div className="h-8 w-8 rounded-sm bg-zinc-800 flex items-center justify-center text-xs font-semibold text-white">
            {(user?.name || "A").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium text-white truncate" data-testid="sidebar-user-name">{user?.name || "Admin"}</div>
            <div className="text-[10px] text-zinc-500 truncate">{user?.email}</div>
          </div>
          <button
            onClick={onLogout}
            data-testid="sidebar-logout-btn"
            className="text-zinc-400 hover:text-white transition-colors duration-150"
            title="Sign out"
          >
            <SignOut size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function SideLink({ to, label, icon: Icon, end, onNavigate }) {
  return (
    <li>
      <NavLink
        to={to}
        end={end}
        onClick={onNavigate}
        data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className={({ isActive }) =>
          [
            "group flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors duration-150",
            isActive
              ? "bg-zinc-900 text-white border-l-2 border-blue-500 pl-[10px]"
              : "text-zinc-400 hover:text-white hover:bg-zinc-900",
          ].join(" ")
        }
      >
        <Icon size={16} weight="regular" />
        <span>{label}</span>
      </NavLink>
    </li>
  );
}

export function PageHeader({ title, subtitle, actions, testId }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6 sm:mb-8" data-testid={testId || "page-header"}>
      <div className="min-w-0">
        <h1 className="font-display text-2xl sm:text-3xl tracking-tight font-medium text-zinc-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-zinc-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Surface({ children, className = "", testId }) {
  return (
    <div
      data-testid={testId}
      className={`bg-white border border-zinc-200 rounded-sm ${className}`}
    >
      {children}
    </div>
  );
}

export function TableScroll({ children, className = "" }) {
  return (
    <div className={`overflow-x-auto ${className}`}>
      {children}
    </div>
  );
}

export function StatusPill({ value }) {
  const map = {
    active: "bg-emerald-50 text-emerald-700 border border-emerald-100",
    approved: "bg-emerald-50 text-emerald-700 border border-emerald-100",
    completed: "bg-emerald-50 text-emerald-700 border border-emerald-100",
    delivered: "bg-emerald-50 text-emerald-700 border border-emerald-100",
    pending: "bg-amber-50 text-amber-700 border border-amber-100",
    pending_activation: "bg-amber-50 text-amber-700 border border-amber-100",
    to_be_bound: "bg-amber-50 text-amber-700 border border-amber-100",
    in_production: "bg-blue-50 text-blue-700 border border-blue-100",
    shipped: "bg-blue-50 text-blue-700 border border-blue-100",
    closing: "bg-zinc-100 text-zinc-600 border border-zinc-200",
    not_submitted: "bg-zinc-100 text-zinc-600 border border-zinc-200",
    submitted: "bg-blue-50 text-blue-700 border border-blue-100",
    frozen: "bg-amber-50 text-amber-700 border border-amber-100",
    locked: "bg-red-50 text-red-700 border border-red-100",
    expired: "bg-zinc-100 text-zinc-600 border border-zinc-200",
    suspended: "bg-red-50 text-red-700 border border-red-100",
    rejected: "bg-red-50 text-red-700 border border-red-100",
    failed: "bg-red-50 text-red-700 border border-red-100",
    terminated: "bg-zinc-100 text-zinc-600 border border-zinc-200",
  };
  const cls = map[(value || "").toLowerCase()] || "bg-zinc-100 text-zinc-700 border border-zinc-200";
  return (
    <span className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-[0.05em] px-2 py-0.5 rounded-sm ${cls}`}>
      {value || "—"}
    </span>
  );
}
